import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Play, Pause, SkipBack, SkipForward, UserCircle } from "@phosphor-icons/react";
import BackButton from "../../components/ui/BackButton";
import duasJson from "../../data/duasAudio.json";

type Reciter = { id: string; name: string; url: string; avatar?: string; avatarUrl?: string };
type Dua = { id: string; title: string; reciters: Reciter[] };

const fmt = (ms: number) => { const t=Math.max(0,Math.floor(ms/1000)); const m=Math.floor(t/60); const s=String(t%60).padStart(2,"0"); return `${m}:${s}`; };
const getAvatarUrl = (r?: Reciter) => r?.avatarUrl || (r?.avatar ? `/images/readers/${r.avatar}.jpg` : "/images/readers/placeholder.jpg");

export default function DuaPlayerClient({ duaId }: { duaId: string }) {
  const dua = useMemo(() => (duasJson as Dua[]).find((d) => d.id === duaId), [duaId]);

  const [reciterIndex, setReciterIndex] = useState(0);
  const reciters = dua?.reciters ?? [];
  const reciter = reciters[reciterIndex];

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [showReciters, setShowReciters] = useState(false);

  useEffect(() => { if (dua?.title) document.title = `${dua.title} | نور على نور`; }, [dua?.title]);

  useEffect(() => {
    if (!reciter?.url) return;
    const audio = new Audio(reciter.url);
    audioRef.current = audio;
    setIsBuffering(true); setIsPlaying(false); setPosition(0); setDuration(0);

    const onLoaded = () => { setDuration(Math.round((audio.duration||0)*1000)); setIsBuffering(false); };
    const onTime = () => setPosition(Math.round(audio.currentTime*1000));
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onError = () => setIsBuffering(false);

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("canplay", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("error", onError);

    audio.play().catch(() => {});
    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("canplay", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
    };
  }, [reciter?.url]);

  const safeDur = Math.max(0, duration || 0);
  const safePos = Math.min(Math.max(0, position || 0), safeDur);

  const toggle = () => { const a=audioRef.current; if(!a) return; (isPlaying? a.pause() : a.play().catch(()=>{})); };
  const seekTo = (ms:number) => { const a=audioRef.current; if(!a) return; const v=Math.max(0,Math.min(ms,safeDur)); a.currentTime=v/1000; setPosition(v); };

  if (!dua) {
    return (
      <EmptyWrap>
        <BackButton href="/duas" />
        <h2>لم يتم العثور على الدعاء</h2>
      </EmptyWrap>
    );
  }

  return (
    <Wrap>
      <TopBar>
        <BackButton href="/duas" />
        <Title>{dua.title}</Title>
        <div style={{ width: 32 }} />
      </TopBar>

      <Byline>بصوت: <strong>{reciter?.name ?? "غير معروف"}</strong></Byline>
      <ChangeBtn onClick={() => setShowReciters(true)}>تغيير القارئ</ChangeBtn>

      <ImageBox>
        <img src={getAvatarUrl(reciter)} alt={reciter?.name ?? "reciter"} />
        {isBuffering && <LoaderOverlay>… جاري التحميل</LoaderOverlay>}
      </ImageBox>

      <LTR>
        <SliderWrap>
          <input type="range" min={0} max={safeDur || 1} value={safePos} onChange={(e)=>seekTo(Number(e.target.value))} disabled={!safeDur}/>
        </SliderWrap>
        <TimeRow>
          <span>{safeDur ? fmt(safePos) : "--:--"}</span>
          <span>{safeDur ? fmt(safeDur - safePos) : "--:--"}</span>
        </TimeRow>
        <Controls>
          <IconBtn onClick={()=>seekTo(Math.max(0, safePos-15000))} aria-label="رجوع 15 ثانية" disabled={!safeDur}><SkipBack size={28}/></IconBtn>
          <PlayBtn onClick={toggle} aria-label={isPlaying ? "إيقاف مؤقت" : "تشغيل"} disabled={!reciter?.url}>
            {isPlaying ? <Pause size={40}/> : <Play size={40}/>}
          </PlayBtn>
          <IconBtn onClick={()=>seekTo(safePos+15000)} aria-label="تقديم 15 ثانية" disabled={!safeDur}><SkipForward size={28}/></IconBtn>
        </Controls>
      </LTR>

      {showReciters && (
        <ModalBackdrop onClick={()=>setShowReciters(false)}>
          <ModalCard onClick={(e)=>e.stopPropagation()}>
            <ModalTitle>اختر القارئ</ModalTitle>
            <ReciterList>
              {dua.reciters.map((r, i) => {
                const active = i === reciterIndex;
                const avatar = getAvatarUrl(r);
                return (
                  <ReciterRow key={r.id} $active={active} onClick={()=>{ setReciterIndex(i); setShowReciters(false); }}>
                    <Avatar>{avatar ? <img src={avatar} alt={r.name}/> : <UserCircle size={28}/>}</Avatar>
                    <span>{r.name}</span>
                  </ReciterRow>
                );
              })}
            </ReciterList>
            <CloseBtn onClick={()=>setShowReciters(false)}>إغلاق</CloseBtn>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Wrap>
  );
}

/* styles */
const Wrap = styled.div` min-height:100dvh; padding:20px clamp(16px,4vw,32px) 32px; background:var(--background); color:var(--text); display:flex; flex-direction:column; `;
const EmptyWrap = styled(Wrap)` align-items:center; justify-content:center; gap:12px; `;
const TopBar = styled.div`
  position: relative;              /* <-- add this */
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  align-items: center;
  justify-items: center;
  margin-bottom: 8px;
`;
const Title = styled.h1`
  grid-column: 2;                  /* be explicit: middle cell */
  margin: 0;
  justify-self: center;
  text-align: center;
  direction: rtl;                  /* Arabic shaping & punctuation */
  font-family: "Lateef", serif;
  font-weight: 500;
  font-size: clamp(40px, 5.5vw, 44px);
  line-height: 1.05;
  color: var(--primary);
  max-width: 16ch;                 /* keeps two tidy lines */
  text-wrap: balance;              /* modern browsers center each line */
  white-space: normal;
`;
const Byline = styled.p` text-align:center; color:var(--muted); margin:6px 0 10px; `;
const ChangeBtn = styled.button` align-self:center; padding:8px 14px; border-radius:20px; background:var(--surface); border:1px solid var(--border); color:var(--primary); cursor:pointer; margin-bottom:12px; `;
const ImageBox = styled.div` width:min(270px,92%); align-self:center; border-radius:16px; overflow:hidden; margin-bottom:14px; box-shadow:var(--shadow); position:relative; img{ width:100%; height:260px; object-fit:cover; display:block; } `;
const LoaderOverlay = styled.div` position:absolute; inset:0; display:grid; place-items:center; background:rgba(0,0,0,.35); color:#fff; font-weight:600; `;
const LTR = styled.div` direction:ltr; unicode-bidi:isolate; width:100%; display:flex; flex-direction:column; align-items:center; `;
const SliderWrap = styled.div` width:100%; max-width:520px; margin-top:8px; input[type="range"]{ direction:ltr; width:100%; appearance:none; height:4px; border-radius:999px; background:var(--border); outline:none; } input[type="range"]::-webkit-slider-thumb{ appearance:none; height:14px; width:14px; border-radius:50%; background:var(--primary); margin-top:-5px; cursor:pointer; } input[type="range"]::-moz-range-thumb{ height:14px; width:14px; border-radius:50%; background:var(--primary); cursor:pointer; } `;
const TimeRow = styled.div` direction:ltr; display:flex; justify-content:space-between; width:100%; max-width:520px; margin:6px 0 18px; font-weight:700; `;
const Controls = styled.div` direction:ltr; display:flex; gap:24px; align-items:center; justify-content:center; `;
const IconBtn = styled.button<{disabled?:boolean}>` height:48px; width:48px; border-radius:999px; border:1px solid var(--border); background:var(--surface); color:var(--text); display:grid; place-items:center; cursor:pointer; opacity:${p=>p.disabled?0.6:1}; `;
const PlayBtn = styled(IconBtn)` height:72px; width:72px; background:var(--primary); color:#fff; border:none; `;
const ModalBackdrop = styled.div` position:fixed; inset:0; background:rgba(0,0,0,.45); display:grid; place-items:center; padding:20px; z-index:50; `;
const ModalCard = styled.div` width:100%; max-width:480px; background:var(--surface); border:1px solid var(--border); border-radius:16px; padding:14px; box-shadow:var(--shadow); `;
const ModalTitle = styled.h3` margin:0 0 10px; text-align:center; color:var(--primary); font-weight:700; `;
const ReciterList = styled.div` max-height:min(60vh,420px); overflow:auto; padding-right:4px; `;
const ReciterRow = styled.button<{ $active?: boolean }>` width:100%; display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:10px; border:1px solid ${p=>p.$active?"var(--primary)":"var(--border)"}; background:${p=>p.$active?"var(--primary)":"transparent"}; color:${p=>p.$active?"#fff":"var(--text)"}; cursor:pointer; margin-bottom:8px; text-align:right; `;
const Avatar = styled.div` width:46px; height:46px; border-radius:14px; overflow:hidden; background:var(--border); display:grid; place-items:center; img{ width:100%; height:100%; object-fit:cover; display:block; } `;
const CloseBtn = styled.button` display:block; margin:8px auto 0; padding:6px 14px; border-radius:12px; border:1px solid var(--border); background:var(--surface); cursor:pointer; `;
